import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, Mail, BellRing, ListChecks, FileSpreadsheet, Archive, UserPlus } from 'lucide-react';
import {
  useOnboardingPeople,
  useSendInvites,
  useSendReminders,
  useResendInvite,
  useSetOnboardingVisibility,
  useSetRemindersMuted,
  useFinishOnboarding,
  onboardingPersonStatus,
  type OnboardingPerson,
} from '../../hooks/onboardingOps';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { PageHeader } from '../../components/ui/PageHeader';
import { MetricBlock } from '../../components/ui/MetricBlock';
import { EmptyState } from '../../components/ui/EmptyState';
import { BulkAssignModal } from '../../components/onboarding/BulkAssignModal';
import { DashboardPersonRow } from '../../components/onboarding/DashboardPersonRow';
import {
  DashboardFilters,
  type DashboardStartWindow as StartWindow,
  type DashboardStatusFilter as Status,
} from '../../components/onboarding/DashboardFilters';
import { useCurrentScope } from '../../hooks/useMobilisations';
import { useCurrentUser } from '../../hooks/useAuth';
import { canRunWizard } from './NewEmployee/canRunWizard';
import { ApiError } from '../../lib/api';

function matchesStatus(p: OnboardingPerson, s: Status): boolean {
  return s === 'all' || onboardingPersonStatus(p) === s;
}

/** Days from today (local midnight) to the given YYYY-MM-DD date; null if unparseable. */
function daysUntil(date: string | null): number | null {
  if (!date) return null;
  const target = new Date(date + 'T00:00:00');
  if (Number.isNaN(target.getTime())) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

function matchesStartWindow(p: OnboardingPerson, w: StartWindow): boolean {
  if (w === 'any') return true;
  if (w === 'none') return !p.start_date;
  const d = daysUntil(p.start_date);
  if (d === null) return false;
  return w === 'week' ? d >= 0 && d <= 7 : d >= 0 && d <= 30; // upcoming window
}

export function OnboardingDashboard() {
  const { data: user } = useCurrentUser();
  const [showFinished, setShowFinished] = useState(false);
  const people = useOnboardingPeople(showFinished);
  const invites = useSendInvites();
  const reminders = useSendReminders();
  const resend = useResendInvite();
  const visibility = useSetOnboardingVisibility();
  const remindersMuted = useSetRemindersMuted();
  const finish = useFinishOnboarding();
  // Only the durable onboarding programme is the "day-to-day" scope. There, the
  // dashboard splits from mobilisation days: show only people who aren't
  // allocated to a dated mobilisation. In a mobilisation / All scope it shows
  // everyone, so the cockpit and its counters stay complete.
  const scope = useCurrentScope();
  const dayToDay = scope?.kind === 'onboarding';

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<Status>('all');
  const [contract, setContract] = useState<string>('all');
  const [startWindow, setStartWindow] = useState<StartWindow>('any');
  const [showHidden, setShowHidden] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [confirmFinish, setConfirmFinish] = useState(false);

  const all = people.data ?? [];
  const hiddenCount = all.filter((p) => p.hidden).length;
  const finishedCount = all.filter((p) => p.finished).length;
  const contracts = useMemo(
    () => [...new Set(all.map((p) => p.contract).filter((c): c is string => !!c))].sort(),
    [all],
  );

  const rows = all.filter((p) => {
    if (p.hidden && !showHidden) return false;
    // Day-to-day scope excludes anyone allocated to a mobilisation day; they
    // are managed under that mobilisation's scope instead.
    if (dayToDay && p.on_mobilisation) return false;
    if (q && !`${p.full_name} ${p.email}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (!matchesStatus(p, status)) return false;
    if (contract !== 'all' && p.contract !== contract) return false;
    if (!matchesStartWindow(p, startWindow)) return false;
    return true;
  });

  // Metrics over the active cohort shown in the list: non-hidden, not finished,
  // and (in the day-to-day scope only) not allocated to a mobilisation day, so
  // the counters always match the rows below. In a mobilisation / All scope,
  // mobilisation people are counted too.
  const totals = all
    .filter((p) => !p.hidden && !p.finished && !(dayToDay && p.on_mobilisation))
    .reduce(
      (acc, p) => {
        acc.people += 1;
        const s = onboardingPersonStatus(p);
        if (s === 'complete') acc.complete += 1;
        else if (s === 'awaiting_approval') acc.awaitingApproval += 1;
        else if (s === 'in_progress') acc.inProgress += 1;
        else acc.notStarted += 1;
        return acc;
      },
      { people: 0, complete: 0, awaitingApproval: 0, inProgress: 0, notStarted: 0 },
    );

  const visibleIds = rows.map((p) => p.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  // Only finish people who are not already finished (a selection can include
  // finished rows when "Show finished" is on).
  const finishTargets = [...selected].filter(
    (id) => !all.find((p) => p.id === id)?.finished,
  );

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleSelectAll() {
    setSelected((prev) => {
      if (visibleIds.every((id) => prev.has(id))) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleIds]);
    });
  }

  // Targeted sends: pass the ticked people. Reminders keep a blanket option
  // (they only reach already-invited people with outstanding items); cold
  // invites are always targeted to whoever is selected.
  async function runInvites(ids: string[]) {
    try {
      const r = await invites.mutateAsync(ids);
      toast.success(`Sent ${r.sent} of ${r.attempted} invites`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed');
    }
  }
  async function runReminders(ids?: string[]) {
    try {
      const r = await reminders.mutateAsync(ids);
      toast.success(`Sent ${r.sent} reminder(s)`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed');
    }
  }
  async function setHidden(p: OnboardingPerson, hidden: boolean) {
    try {
      await visibility.mutateAsync({ personId: p.id, hidden });
      toast.success(hidden ? `Hidden ${p.full_name}` : `Restored ${p.full_name}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed');
    }
  }
  async function setMuted(p: OnboardingPerson, muted: boolean) {
    try {
      await remindersMuted.mutateAsync({ personId: p.id, muted });
      toast.success(
        muted
          ? `Paused automated reminders for ${p.full_name}`
          : `Resumed automated reminders for ${p.full_name}`,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed');
    }
  }
  // Finishing is reversible (Reopen / Show finished), so a single row acts
  // immediately like Hide. The bulk path confirms first since it touches many.
  async function toggleFinished(p: OnboardingPerson) {
    try {
      await finish.mutateAsync({
        personId: p.id,
        finished: !p.finished,
        reason: p.finished ? undefined : 'onboarding complete',
      });
      toast.success(p.finished ? `Reopened ${p.full_name}` : `Finished ${p.full_name}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed');
    }
  }
  async function runBulkFinish(ids: string[]) {
    try {
      const results = await Promise.allSettled(
        ids.map((id) =>
          finish.mutateAsync({ personId: id, finished: true, reason: 'onboarding complete' }),
        ),
      );
      const done = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - done;
      toast[failed ? 'warning' : 'success'](
        `Finished ${done} of ${results.length}${failed ? `; ${failed} failed` : ''}`,
      );
      setSelected(new Set());
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed');
    }
  }

  return (
    <main className="mx-auto max-w-7xl space-y-5 px-4 py-8">
      <PageHeader
        eyebrowIcon={Users}
        eyebrow="Onboarding"
        title="Onboarding dashboard"
        description="Track new starters' progress, chase outstanding tasks, and export captured data."
        trailing={
          <div className="flex flex-wrap gap-2">
            {canRunWizard(user?.roles ?? []) ? (
              <Link to="/onboarding/new">
                <Button variant="primary" size="sm" iconLeft={<UserPlus className="h-4 w-4" />}>
                  New employee
                </Button>
              </Link>
            ) : null}
            <Button variant="ghost" size="sm" iconLeft={<BellRing className="h-4 w-4" />} onClick={() => runReminders()} loading={reminders.isPending}>
              Remind all
            </Button>
            <a href="/api/admin/onboarding/submissions.xlsx">
              <Button variant="ghost" size="sm" iconLeft={<FileSpreadsheet className="h-4 w-4" />}>
                Export Excel
              </Button>
            </a>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <MetricBlock label="People" value={String(totals.people)} />
        <MetricBlock label="Complete" value={String(totals.complete)} />
        <MetricBlock label="Pending approval" value={String(totals.awaitingApproval)} />
        <MetricBlock label="In progress" value={String(totals.inProgress)} />
        <MetricBlock label="Not started" value={String(totals.notStarted)} />
      </div>

      <DashboardFilters
        q={q}
        onQ={setQ}
        contracts={contracts}
        contract={contract}
        onContract={setContract}
        startWindow={startWindow}
        onStartWindow={setStartWindow}
        hiddenCount={hiddenCount}
        showHidden={showHidden}
        onToggleShowHidden={() => setShowHidden((v) => !v)}
        finishedCount={finishedCount}
        showFinished={showFinished}
        onToggleShowFinished={() => setShowFinished((v) => !v)}
        status={status}
        onStatus={setStatus}
      />

      {selected.size > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-token-sm border border-accent/20 bg-accent/10 px-4 py-2.5">
          <span className="text-sm font-medium text-fg">{selected.size} selected</span>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<Mail className="h-4 w-4" />} loading={invites.isPending} onClick={() => runInvites([...selected])}>
              Invite
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<BellRing className="h-4 w-4" />} loading={reminders.isPending} onClick={() => runReminders([...selected])}>
              Remind
            </Button>
            <Button variant="primary" size="sm" iconLeft={<ListChecks className="h-4 w-4" />} onClick={() => setAssignOpen(true)}>
              Assign tasks
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Archive className="h-4 w-4" />}
              disabled={finishTargets.length === 0}
              onClick={() => setConfirmFinish(true)}
            >
              Finish onboarding
            </Button>
          </div>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <EmptyState icon={<Users className="h-6 w-6" />} title="No matching people" description="Adjust the filters, import new starters, or show hidden people." />
      ) : (
        <Card>
          <div className="flex items-center gap-3 border-b border-divider/10 px-5 py-2.5 text-xs font-medium text-fg-muted">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} aria-label="Select all" />
            <span>Select all ({rows.length})</span>
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() =>
                  setExpanded(new Set(rows.filter((p) => p.tasks.length > 0).map((p) => p.id)))
                }
                className="rounded-token-sm px-2 py-1 transition-colors hover:bg-surface-2 hover:text-fg"
              >
                Expand all
              </button>
              <button
                type="button"
                onClick={() => setExpanded(new Set())}
                className="rounded-token-sm px-2 py-1 transition-colors hover:bg-surface-2 hover:text-fg"
              >
                Collapse all
              </button>
            </div>
          </div>
          <ul className="divide-y divide-divider/10">
            {rows.map((p) => (
              <DashboardPersonRow
                key={p.id}
                person={p}
                selected={selected.has(p.id)}
                expanded={expanded.has(p.id)}
                onToggleSelect={() => toggleSelected(p.id)}
                onToggleExpand={() => toggleExpanded(p.id)}
                onToggleHidden={() => setHidden(p, !p.hidden)}
                onToggleMuted={() => setMuted(p, !p.reminders_muted)}
                onToggleFinished={() => toggleFinished(p)}
                onResendInvite={async () => {
                  const r = await resend.mutateAsync(p.id);
                  toast[r.sent ? 'success' : 'error'](r.sent ? 'Invite resent' : 'No email on file');
                }}
              />
            ))}
          </ul>
        </Card>
      )}

      {assignOpen ? (
        <BulkAssignModal
          personIds={[...selected]}
          onClose={() => {
            setAssignOpen(false);
            setSelected(new Set());
          }}
        />
      ) : null}

      <ConfirmModal
        open={confirmFinish}
        tone="warning"
        icon={Archive}
        title="Finish onboarding?"
        message={`Mark ${finishTargets.length} ${
          finishTargets.length === 1 ? 'person' : 'people'
        } as finished. They drop off the active dashboard but stay in reports and exports, and you can reopen them any time from "Show finished".`}
        confirmLabel="Finish onboarding"
        onConfirm={async () => {
          await runBulkFinish(finishTargets);
          setConfirmFinish(false);
        }}
        onClose={() => setConfirmFinish(false)}
      />
    </main>
  );
}
